import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Upload, X } from 'lucide-react';
import { parseSpreadsheetMlRows } from './OrganizationsBulkImportModal';
import {
  getMaterialCategoryPrefixMap,
  getMaterialFamilies,
  HAZARD_CLASSES,
  MATERIAL_STATUSES,
  MATERIAL_UNITS,
  nextMaterialCode,
  upsertMaterialCategory,
} from './rawMaterialsDomain';

const TEMPLATE_HEADERS = [
  'family',
  'family_prefix',
  'code',
  'name',
  'chemical_name',
  'cas_number',
  'appearance',
  'hazard_class',
  'status',
  'unit',
  'unit_cost',
  'min_stock',
  'typical_supplier',
  'packing',
  'storage',
  'notes',
  'procurement_code',
];

const COLUMN_LABELS = {
  family: 'Categoría',
  family_prefix: 'Prefijo',
  code: 'Código producción',
  name: 'Nombre comercial',
  chemical_name: 'Nombre químico',
  cas_number: 'N° CAS',
  appearance: 'Apariencia',
  hazard_class: 'Clase de peligro',
  status: 'Estado',
  unit: 'Unidad',
  unit_cost: 'Costo unitario',
  min_stock: 'Stock mínimo',
  typical_supplier: 'Proveedor habitual',
  packing: 'Envase',
  storage: 'Almacenamiento',
  notes: 'Notas',
  procurement_code: 'Cód. adquisiciones',
};

const TEMPLATE_EXAMPLE_CELLS = [
  'Resinas',
  'RS',
  'RS0099',
  'Acronal Ejemplo',
  'Dispersión acrílica',
  '25068-38-6',
  'Líquido viscoso',
  'No peligroso',
  'Activa',
  'kg',
  '1200',
  '50',
  'Proveedor Ejemplo',
  'Tambor 200 kg',
  'Bodega MP',
  '',
  '',
];

const emptyRow = () =>
  TEMPLATE_HEADERS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});

const escapeXml = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export const downloadRawMaterialsExcelTemplate = () => {
  const headerCells = TEMPLATE_HEADERS.map(
    (header) => `<Cell><Data ss:Type="String">${escapeXml(header)}</Data></Cell>`
  ).join('');
  const exampleCells = TEMPLATE_EXAMPLE_CELLS.map(
    (cell) => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`
  ).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="MateriasPrimas">
  <Table>
   <Row>${headerCells}</Row>
   <Row>${exampleCells}</Row>
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla_materias_primas.xls';
  a.click();
  URL.revokeObjectURL(url);
};

const normalizeHeader = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const HEADER_ALIASES = {
  family: ['family', 'categoria', 'category'],
  family_prefix: ['family_prefix', 'prefijo', 'prefix'],
  code: ['code', 'codigo', 'codigo_produccion', 'sku'],
  name: ['name', 'nombre', 'nombre_comercial'],
  chemical_name: ['chemical_name', 'nombre_quimico'],
  cas_number: ['cas_number', 'cas', 'n_cas', 'numero_cas'],
  appearance: ['appearance', 'apariencia'],
  hazard_class: ['hazard_class', 'clase_de_peligro', 'peligro'],
  status: ['status', 'estado'],
  unit: ['unit', 'unidad'],
  unit_cost: ['unit_cost', 'costo', 'costo_unitario'],
  min_stock: ['min_stock', 'stock_minimo', 'minimo'],
  typical_supplier: ['typical_supplier', 'proveedor', 'proveedor_habitual'],
  packing: ['packing', 'envase', 'presentacion'],
  storage: ['storage', 'almacenamiento'],
  notes: ['notes', 'notas'],
  procurement_code: ['procurement_code', 'codigo_adquisiciones', 'sku_adquisiciones'],
};

const looksLikeBinaryOffice = (text = '') => {
  const sample = String(text || '').slice(0, 8);
  return sample.includes('PK') || sample.includes('\u0000');
};

const looksLikeSpreadsheetMl = (text = '') =>
  /<\s*Workbook[\s>]/i.test(String(text || '')) || /ss:Workbook/i.test(String(text || ''));

const detectDelimiter = (line = '') => {
  const commas = (line.match(/,/g) || []).length;
  const semis = (line.match(/;/g) || []).length;
  return semis >= commas ? ';' : ',';
};

const splitCsvLine = (line = '', delimiter = ';') => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      cells.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
};

const mapHeader = (raw = '') => {
  const key = normalizeHeader(raw);
  return (
    Object.entries(HEADER_ALIASES).find(([, aliases]) => aliases.includes(key))?.[0] ||
    (TEMPLATE_HEADERS.includes(key) ? key : '')
  );
};

const buildRowsFromCellTable = (table = []) => {
  if (!table.length) return { rows: [], error: 'El archivo está vacío.' };
  const mapping = (table[0] || []).map(mapHeader);
  if (!mapping.includes('name')) {
    return { rows: [], error: 'Falta la columna name / nombre en el encabezado.' };
  }
  const rows = [];
  for (let i = 1; i < table.length; i += 1) {
    const cells = table[i] || [];
    if (!cells.some((cell) => String(cell || '').trim())) continue;
    const row = emptyRow();
    mapping.forEach((field, idx) => {
      if (!field) return;
      row[field] = String(cells[idx] || '').trim();
    });
    if (!row.name) continue;
    rows.push(row);
  }
  if (!rows.length) {
    return { rows: [], error: 'No se encontraron filas con nombre de materia prima.' };
  }
  return { rows, error: '' };
};

export const parseRawMaterialsCsv = (rawText = '') => {
  const text = String(rawText || '').replace(/^\uFEFF/, '').trim();
  if (!text) return { rows: [], error: 'Pega o sube un archivo CSV o la plantilla Excel (.xls).' };
  if (looksLikeBinaryOffice(text)) {
    return {
      rows: [],
      error:
        'Este archivo parece .xlsx/.xls binario. Usa la plantilla Excel descargada aquí, o exporta a CSV (separado por ;).',
    };
  }
  if (looksLikeSpreadsheetMl(text)) {
    return buildRowsFromCellTable(parseSpreadsheetMlRows(text));
  }
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return { rows: [], error: 'El archivo está vacío.' };
  const delimiter = detectDelimiter(lines[0]);
  const table = lines.map((line) => splitCsvLine(line, delimiter));
  return buildRowsFromCellTable(table);
};

const foldKey = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const pickOption = (raw, options, fallback = '') => {
  const value = String(raw || '').trim();
  if (!value) return fallback;
  const exact = options.find((opt) => String(opt) === value);
  if (exact) return exact;
  const folded = foldKey(value);
  const soft = options.find((opt) => foldKey(opt) === folded);
  return soft || value;
};

export const homologateRawMaterialImportRow = (row = {}) => {
  const next = { ...emptyRow(), ...row };
  next.family = String(next.family || '').trim() || 'Resinas';
  next.family_prefix = String(next.family_prefix || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  next.code = String(next.code || '').trim().toUpperCase();
  next.name = String(next.name || '').trim();
  next.hazard_class = pickOption(next.hazard_class, HAZARD_CLASSES, 'No peligroso');
  next.status = pickOption(next.status, MATERIAL_STATUSES, 'Activa');
  next.unit = pickOption(next.unit, MATERIAL_UNITS, 'kg');
  next.unit_cost = String(next.unit_cost || '').trim();
  next.min_stock = String(next.min_stock || '').trim();
  next.procurement_code = String(next.procurement_code || '').trim().toUpperCase();
  return next;
};

export const getRawMaterialImportFieldErrors = (row = {}) => {
  const errors = {};
  if (!String(row.name || '').trim()) errors.name = 'Obligatorio';
  if (!String(row.family || '').trim()) errors.family = 'Obligatorio';
  const code = String(row.code || '').trim();
  if (code && !/^[A-Z]{2}\d{4}$/i.test(code)) errors.code = 'Formato LL####';
  const prefix = String(row.family_prefix || '').trim();
  if (prefix && !/^[A-Z]{2}$/.test(prefix)) errors.family_prefix = '2 letras';
  return errors;
};

export const toRawMaterialApiPayload = (row, existingMaterials = []) => {
  const family = String(row.family || 'Resinas').trim() || 'Resinas';
  const prefixHint = String(row.family_prefix || '').trim().toUpperCase();
  const map = getMaterialCategoryPrefixMap();
  if (!map[family] && prefixHint) {
    upsertMaterialCategory({ name: family, prefix: prefixHint });
  } else if (!map[family] && row.code) {
    upsertMaterialCategory({ name: family, prefix: String(row.code).slice(0, 2).toUpperCase() });
  }
  const code =
    String(row.code || '').trim().toUpperCase() || nextMaterialCode(existingMaterials, family);
  return {
    code,
    family,
    name: String(row.name || '').trim(),
    chemical_name: String(row.chemical_name || '').trim(),
    cas_number: String(row.cas_number || '').trim(),
    appearance: String(row.appearance || '').trim(),
    hazard_class: String(row.hazard_class || 'No peligroso').trim() || 'No peligroso',
    status: String(row.status || 'Activa').trim() || 'Activa',
    unit: String(row.unit || 'kg').trim() || 'kg',
    unit_cost: Number(String(row.unit_cost || '0').replace(',', '.')) || 0,
    min_stock: Number(String(row.min_stock || '0').replace(',', '.')) || 0,
    typical_supplier: String(row.typical_supplier || '').trim(),
    packing: String(row.packing || '').trim(),
    storage: String(row.storage || '').trim(),
    notes: String(row.notes || '').trim(),
    has_sds: true,
    procurement_code: String(row.procurement_code || '').trim().toUpperCase(),
    procurement_item_id: null,
    tech_params: [],
  };
};

const PREVIEW_COLUMNS = [
  'family',
  'family_prefix',
  'code',
  'name',
  'chemical_name',
  'cas_number',
  'hazard_class',
  'status',
  'unit',
  'unit_cost',
  'min_stock',
  'procurement_code',
];

const RawMaterialsBulkImportModal = ({
  isOpen = false,
  onClose,
  onImport,
  isImporting = false,
  isDarkMode = false,
  result = null,
}) => {
  const [step, setStep] = useState('source');
  const [rawText, setRawText] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setStep('source');
    setRawText('');
    setPreviewRows([]);
    setLocalError('');
    setLocalSuccess('');
  }, [isOpen]);

  const colors = {
    text: isDarkMode ? '#f4f4f5' : '#0f172a',
    muted: isDarkMode ? '#a1a1aa' : '#64748b',
    border: isDarkMode ? '#3f3f46' : '#e2e8f0',
    inputBg: isDarkMode ? '#18181b' : '#fff',
    inputBorder: isDarkMode ? '#3f3f46' : '#cbd5e1',
    panel: isDarkMode ? '#09090b' : '#fff',
  };

  const parsed = useMemo(() => parseRawMaterialsCsv(rawText), [rawText]);
  const families = useMemo(() => getMaterialFamilies(), [isOpen, previewRows.length]);

  const rowErrors = useMemo(
    () => previewRows.map((row) => getRawMaterialImportFieldErrors(row)),
    [previewRows]
  );
  const invalidFieldCount = rowErrors.reduce((sum, err) => sum + Object.keys(err).length, 0);

  if (!isOpen) return null;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      setRawText(text);
      setLocalError('');
      setLocalSuccess('');
    } catch {
      setLocalError('No se pudo leer el archivo.');
    }
  };

  const handleContinueFromSource = () => {
    if (parsed.error || !parsed.rows.length) {
      setLocalError(parsed.error || 'No hay filas para revisar.');
      return;
    }
    setPreviewRows(parsed.rows.map((row) => homologateRawMaterialImportRow(row)));
    setStep('preview');
    setLocalError('');
    setLocalSuccess('');
  };

  const updatePreviewCell = (index, key, value) => {
    setPreviewRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );
  };

  const removePreviewRow = (index) => {
    setPreviewRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!previewRows.length || invalidFieldCount > 0 || isImporting || localSuccess) return;
    setLocalError('');
    try {
      const data = await onImport?.(previewRows);
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      const errors = Number(data?.errors || 0);
      if (errors === 0 || created > 0) {
        setLocalSuccess(
          `Importación exitosa: ${created} materia(s) prima(s) creadas` +
            (skipped ? `, ${skipped} omitidas` : '') +
            '.'
        );
      }
    } catch (err) {
      setLocalError(err?.message || 'No se pudo importar.');
    }
  };

  const inputStyle = {
    width: '100%',
    minWidth: 90,
    height: 30,
    borderRadius: 6,
    border: `1px solid ${colors.inputBorder}`,
    background: colors.inputBg,
    color: colors.text,
    padding: '0 8px',
    fontSize: 12,
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => !isImporting && onClose?.()}
    >
      <div
        style={{
          width: 'min(1100px, 100%)',
          maxHeight: '92vh',
          overflow: 'auto',
          background: colors.panel,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          padding: 16,
          display: 'grid',
          gap: 12,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: colors.text, fontSize: 18 }}>
              {step === 'preview' ? 'Revisar importación' : 'Carga masiva de materias primas'}
            </h3>
            <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: 13 }}>
              {step === 'preview'
                ? 'Edita las celdas si hace falta. Los códigos duplicados se omiten al importar.'
                : 'Sube la plantilla Excel (.xls) o un CSV (;). Luego revisas la tabla antes de importar.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            style={{
              border: 'none',
              background: 'transparent',
              color: colors.muted,
              cursor: 'pointer',
              padding: 4,
            }}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {step === 'source' ? (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: colors.inputBg,
                  color: colors.text,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <Upload size={16} />
                Subir archivo
                <input type="file" accept=".csv,.xls,.txt" onChange={handleFile} hidden />
              </label>
              <button
                type="button"
                onClick={downloadRawMaterialsExcelTemplate}
                style={{
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Descargar plantilla
              </button>
            </div>
            <textarea
              value={rawText}
              onChange={(event) => setRawText(event.target.value)}
              placeholder="Pega aquí el CSV o el contenido de la plantilla…"
              rows={10}
              style={{
                width: '100%',
                borderRadius: 8,
                border: `1px solid ${colors.inputBorder}`,
                background: colors.inputBg,
                color: colors.text,
                padding: 10,
                fontSize: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleContinueFromSource}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#2563eb',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Continuar
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
                <thead>
                  <tr>
                    <th style={{ padding: 8, fontSize: 11, color: colors.muted, textAlign: 'left' }}>#</th>
                    {PREVIEW_COLUMNS.map((key) => (
                      <th
                        key={key}
                        style={{ padding: 8, fontSize: 11, color: colors.muted, textAlign: 'left', whiteSpace: 'nowrap' }}
                      >
                        {COLUMN_LABELS[key]}
                      </th>
                    ))}
                    <th style={{ padding: 8 }} />
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => {
                    const errors = rowErrors[index] || {};
                    return (
                      <tr key={`row-${index}`} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td style={{ padding: 6, fontSize: 12, color: colors.muted }}>{index + 1}</td>
                        {PREVIEW_COLUMNS.map((key) => (
                          <td key={key} style={{ padding: 4 }}>
                            {key === 'family' ? (
                              <select
                                value={row.family || ''}
                                onChange={(event) => updatePreviewCell(index, key, event.target.value)}
                                style={{
                                  ...inputStyle,
                                  borderColor: errors[key] ? '#dc2626' : colors.inputBorder,
                                }}
                              >
                                {[row.family, ...families]
                                  .filter(Boolean)
                                  .filter((item, idx, arr) => arr.indexOf(item) === idx)
                                  .map((item) => (
                                    <option key={item} value={item}>
                                      {item}
                                    </option>
                                  ))}
                              </select>
                            ) : key === 'hazard_class' ? (
                              <select
                                value={row.hazard_class || ''}
                                onChange={(event) => updatePreviewCell(index, key, event.target.value)}
                                style={inputStyle}
                              >
                                {HAZARD_CLASSES.map((item) => (
                                  <option key={item} value={item}>
                                    {item}
                                  </option>
                                ))}
                              </select>
                            ) : key === 'status' ? (
                              <select
                                value={row.status || ''}
                                onChange={(event) => updatePreviewCell(index, key, event.target.value)}
                                style={inputStyle}
                              >
                                {MATERIAL_STATUSES.map((item) => (
                                  <option key={item} value={item}>
                                    {item}
                                  </option>
                                ))}
                              </select>
                            ) : key === 'unit' ? (
                              <select
                                value={row.unit || ''}
                                onChange={(event) => updatePreviewCell(index, key, event.target.value)}
                                style={inputStyle}
                              >
                                {MATERIAL_UNITS.map((item) => (
                                  <option key={item} value={item}>
                                    {item}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                value={row[key] || ''}
                                onChange={(event) => updatePreviewCell(index, key, event.target.value)}
                                style={{
                                  ...inputStyle,
                                  borderColor: errors[key] ? '#dc2626' : colors.inputBorder,
                                }}
                              />
                            )}
                          </td>
                        ))}
                        <td style={{ padding: 4, textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => removePreviewRow(index)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#dc2626',
                              cursor: 'pointer',
                            }}
                            aria-label="Quitar fila"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {invalidFieldCount > 0 ? (
              <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>
                Hay {invalidFieldCount} campo(s) con error. Corrígelos antes de importar.
              </p>
            ) : null}
            {result?.error_rows?.length ? (
              <div style={{ fontSize: 12, color: '#dc2626' }}>
                {(result.error_rows || []).slice(0, 8).map((row) => (
                  <div key={`${row.row}-${row.detail}`}>
                    Fila {row.row}: {row.detail}
                  </div>
                ))}
              </div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setStep('source')}
                disabled={isImporting}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                }}
              >
                Volver
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isImporting || invalidFieldCount > 0 || !previewRows.length || Boolean(localSuccess)}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: isImporting || invalidFieldCount > 0 ? colors.muted : '#16a34a',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: isImporting || invalidFieldCount > 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {isImporting ? 'Importando…' : `Importar ${previewRows.length} fila(s)`}
              </button>
            </div>
          </>
        )}

        {localError ? <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>{localError}</p> : null}
        {localSuccess ? <p style={{ margin: 0, fontSize: 12, color: '#16a34a' }}>{localSuccess}</p> : null}
      </div>
    </div>
  );
};

export default RawMaterialsBulkImportModal;
